/** @module csi/property/Splitter */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-geometry",
	"dojo/dom-style",
	"dojo/query",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/printUtil"
], function(declare, domClass, domConstruct, domGeometry, domStyle, query, csi, _, html, locale, printUtil) {

	var SPLIT_ATTR = "csiSplitRow";

	function _findImageNode(rootNode) {
		var n = query(".propertyViewImage", rootNode)[0];
		return n && n.firstChild;
	}

	function _findColumnsNode(rootNode) {
		var n = query(".propertyViewHeadings", rootNode)[0];
		return n && n.firstChild;
	}

	function _findLastRowWithinHeight(/*DOMNode*/tbody, /*Number*/hMax, /*Boolean*/bCanRemoveAll) {
		// summary: return child row of body that fits within given height
		var i = csi.locate(tbody.childNodes, function(domNode) {
			return (domNode.offsetTop + domNode.offsetHeight) > hMax;
		});

		// if no rows fit, and not allowed to remove all rows, then force at least one row to be there
		if (i === 0) {
			return bCanRemoveAll ? null : tbody.firstChild;
		}

		return i === -1 ? tbody.lastChild : tbody.childNodes[i - 1];
	}

	function _getGroupTitle(/*DOMNode*/domRoot) {
		return html.queryDom([".", html.CSS_VIEW_TITLE], domRoot, true);
	}

	function _splitTable(/*DOMNode*/tbodyGroup, /*Number*/hTarget, /*Boolean*/bCanRemoveAll) {
		// summary: given table body, split into two with the first having a height less than hTarget;
		// return second table body containing remaining properties
		var trHeader = tbodyGroup.firstChild;
		var trGroup = tbodyGroup.lastChild;
		var tbodySplit;
		var bSplit;

		// cannot split single property spanning all columns, so return null
		if (query(".csiHeadingColumnSpan", trGroup).length) {
			return bCanRemoveAll ? null : tbodyGroup;
		}

		// subtract group header height from target
		var hMax = (trHeader == trGroup) ? hTarget : (hTarget - domGeometry.getMarginBox(trHeader).h);

		// find rows that fit within max height
		var bodies = query(".csiHeadingColumn tbody", trGroup);
		var rows = bodies.map(function(tbody) {
			return _findLastRowWithinHeight(tbody, hMax, bCanRemoveAll);
		});

		// find new maximum height so that columns are equally distributed when split. For example, say first column has many individual cells,
		// and the second column ends with an image that spans the max height - we don't want to split so that the image moves to the
		// next page without the individual cells to its left. In this case we want to move the image and the cells to its left to the
		// next page, even though some of the cells to its left fit on the first page.
		hMax = _.reduce(rows, function(h, row) {
			if (!row) {
				return bCanRemoveAll ? 0 : h;
			}

			var hRow = row.offsetTop + row.offsetHeight;
			if ((hRow < h) && row.nextSibling) {
				return hRow;
			}

			return h;
		}, hMax);

		// if no rows fit on current page, then try next page; this will only happen in bCanRemoveAll is true
		if (!hMax) {
			return null;
		}

		// find rows that will ultimately fit on current page and mark with attribute so they can be found after copying
		rows = bodies.map(function(tbody) {
			var row = _findLastRowWithinHeight(tbody, hMax);
			if (row) {
				row.setAttribute(SPLIT_ATTR, 1);

				// it is possible that all rows fit, so track if we really need to split the group
				bSplit |= !!row.nextSibling;
			}

			return row;
		});

		// copy group and remove rows that fit on first page
		if (bSplit) {
			tbodySplit = tbodyGroup.cloneNode(true);

			if (_getGroupTitle(tbodySplit)) {
				tbodySplit.removeChild(tbodySplit.firstChild);
			}

			html.queryDom(["tr", html.attributeSelector(SPLIT_ATTR, 1)], tbodySplit).forEach(function(row) {
				html.removeRange(row.parentNode.firstChild, row);
			});
		} else {
			// return given group to indicate that split was not necessary
			tbodySplit = tbodyGroup;
		}

		// remove rows that do not fit on first page from original group
		_.each(rows, function(row) {
			if (row) {
				row.removeAttribute(SPLIT_ATTR);
				html.removeRange(row.nextSibling);
			}
		});

		domClass.add(tbodyGroup, "csiHeadingGroupSplit");

		return tbodySplit;
	}

	function _splitHeaderBody(/*DOMNode*/table) {
		// summary: if first body contains group header, then remove all content except for header and return
		var tbody = table.firstChild;
		var tr = tbody.firstChild;
		var titleNode = _getGroupTitle(tr);
		if (titleNode) {
			// add "continued" to title if not there already
			if (titleNode.firstChild == titleNode.lastChild) {
				html.text(titleNode, " (" + locale.resource("Continued") + ")");
			}

			// remove remaining content from body
			html.removeRange(tr.nextSibling);

			return tbody;
		}

		return null;
	}

	var _ColumnSplitter = declare(null, {
		nTables: 0,
		nBodies: 0,
		tbodySplit: null,

		constructor: function(domColumns) {
			this.domColumns = domColumns;
		},

		fit: function(/*Number*/nMaxHeight, /*Boolean*/bCanRemoveAll) {
			// summary: delete tables and table bodies until it fits on page
			var table = this._removeChildren(this.domColumns, nMaxHeight, bCanRemoveAll);

			// track number of tables that fit
			this.nTables = this.domColumns.childNodes.length;

			// return false if all tables were removed so they will be all moved to next page
			if (!table) {
				return this.nTables > 0;
			}

			// add table back to DOM to see if it can be split
			this.domColumns.appendChild(table);

			// track number of table bodies that fit
			var tbody = this._removeChildren(table, nMaxHeight, false);
			this.nBodies = table.childNodes.length;

			// return true if no body to split to indicate the view was split with the given number of tables and bodies
			if (!tbody) {
				return true;
			}

			// add tbody back to DOM and try to split across pages - if any other tables or bodies exist on the page,
			// then allow all of this body to move to next page if necessary
			var diff = nMaxHeight - this.height();
			table.appendChild(tbody);

			// determine if all content of the body that overflows the page can be removed from the page or not; 
			// if there are more tables or bodies to process, then the answer is yes. However, if there is only
			// one extra body, need to determine if it is a title body; if so, then we don't want to leave just the title
			if (!bCanRemoveAll) {
				if (this.nTables || (this.nBodies > 1)) {
					bCanRemoveAll = true;
				} else if ((this.nBodies == 1) && !_getGroupTitle(table.firstChild)) {
					bCanRemoveAll = true;
				}
			}

			this.tbodySplit = _splitTable(tbody, diff, bCanRemoveAll);

			if (this.tbodySplit == tbody) {
				// cannot split and cannot remove, so keep in place
				this.tbodySplit = null;
			} else if (!this.tbodySplit) {
				// body could not be split on this page, so remove tbody and move to next page
				table.removeChild(tbody);

				// return true if any content remains on this page (all content not removed)
				return this.nTables || !!table.firstChild;
			}

			// split succeeded
			++this.nBodies;
			return true;
		},

		height: function() {
			// summary: return height of table
			return this.domColumns.offsetHeight;
		},

		removeFittedElements: function(/*DOMNode*/domColumns) {
			// summary: remove the range of elements we fitted during 'fit' phase from the new container;
			// insert the extra elements that have been split
			if (this.nTables) {
				html.removeRange(domColumns.firstChild, domColumns.childNodes[this.nTables - 1]);
			}

			var table = domColumns.firstChild;
			if (!table) {
				return;
			}

			var nBodies = table.childNodes.length;
			var tbody;

			// if there are more bodies than will be removed below, then copy header so it appears on the next page as well 
			if (nBodies > this.nBodies) {
				tbody = this.nBodies && _splitHeaderBody(table);
			}

			// remove specified number of bodies from table - if there is header body, then leave it in place
			if (this.nBodies && (!tbody || (this.nBodies > 1))) {
				html.removeRange(tbody ? tbody.nextSibling : table.firstChild, table.childNodes[this.nBodies - 1]);
			}

			if (this.tbodySplit) {
				// add split body immediately after header (if any); otherwise add as first child
				domConstruct.place(this.tbodySplit, tbody || table, tbody ? "after" : "first");
			} else if (tbody && (this.nBodies == 1) && (nBodies == 1)) {
				// if only one body and no split body to add, then no need to keep header
				domColumns.removeChild(table);
			}
		},

		_removeChildren: function(/*DOMNode*/domParent, /*Number*/nMaxHeight, /*Boolean*/bCanRemoveAll) {
			// summary: remove last child until it fits
			var domChild, diff;

			while (domParent.firstChild) {
				diff = nMaxHeight - this.height();

				if (diff < 0) {
					domChild = domParent.removeChild(domParent.lastChild);
				} else {
					break;
				}
			}

			if (diff < 0) {
				// if removed all and less than 150 (pick a number) left on current page, then
				// return false indicating nothing fits and move all to next page
				if (bCanRemoveAll && (nMaxHeight < 150)) {
					return null;
				}
			} else if (diff < 150) {
				// less than 150 left on page, so move remaining to next page
				return null;
			}

			return domChild;
		}
	});

	var Splitter = declare(null, {
		/**
		 * Split property view content so it fits nicely on pages with given width and height
		 * @param {DOMNode[]} aPages
		 * @param {DOMNode} viewRoot
		 * @param {number} nPageHeight
		 * @param {number} nPageWidth
		 * @param {Function} [copyCallback] - function called when child view is copied from one page to another
		 */
		split: function(aPages, viewRoot, nPageHeight, nPageWidth, copyCallback) {
			var eView = aPages.last();
			var imageNode = _findImageNode(viewRoot);
			var columnsNode = _findColumnsNode(viewRoot);
			var imageHeight = imageNode ? imageNode.offsetHeight : 0;
			var pageOverhead = eView.offsetHeight - Math.max(imageHeight, columnsNode.offsetHeight);
			var nViewHeight = nPageHeight - pageOverhead;
			var bFirst = true;
			var bRemoveAll;
			var eCopy;
			var splitter;
			var img;

			// use 200 pixels as check for overhead - anything under might just be a titleblock that will appear on every page,
			// so moving to next page will not do any good
			var bCanRemoveAll = (pageOverhead > 200);

			// move whole view to next page if image is too tall for the first page and there is extra content;
			if (imageHeight > nViewHeight) {
				if (bCanRemoveAll) {
					bRemoveAll = true;
				} else {
					img = query("img", imageNode)[0];
					domStyle.set(img, "maxHeight", (img.offsetHeight - imageHeight + nViewHeight) + "px");
				}
			}

			while (bRemoveAll || (columnsNode.offsetHeight > nViewHeight)) {
				// make copy - set ids on elements so we can find corresponding elements in copy
				eCopy = html.cloneElement(eView);
				aPages.push(eCopy);

				// call function each time there is a copy
				if (copyCallback) {
					copyCallback(eView, eCopy);
				}

				// if columns overrun the page, then remove tbody elements from table until it fits -
				// if all tbody nodes deleted, then remove entire view from previous page
				if (!bRemoveAll) {
					splitter = new _ColumnSplitter(columnsNode);
					bRemoveAll = !splitter.fit(nViewHeight, bCanRemoveAll);
				}

				// if the view does not fit at all on the first page, then remove fully
				if (bRemoveAll) {
					// remove current block from eView (last page) so that composite view label is also removed
					if (bFirst) {
						$(eView).find(".csi-split-current-block").last().remove();
					}

					html.destroyElement(viewRoot);
				}

				// now insert copy and remove elements fitted in the previous step from copied view
				eView.parentNode.appendChild(eCopy);
				eView = eCopy;
				imageNode = _findImageNode(eView);
				columnsNode = _findColumnsNode(eView);

				// remove fitted elements from the copy
				if (splitter) {
					splitter.removeFittedElements(columnsNode);
				}

				// if no property content remaining, then remove copy. Make sure to also handle the case where there is
				// just an image in the property view and the view was moved to the next page in a composite view. In that case,
				// it will be the first page and all content was removed.
				if ((!bFirst || !bRemoveAll) && !query("tbody", columnsNode).length) {
					var pageNode = viewRoot && html.getParentByClassName(columnsNode, html.CSS_PAGE);
					if (pageNode && pageNode.nextSibling) {
						// if in HTML view and there is a subsequent view, then just remove page block from copied page
						html.destroyElement(pageNode);
					} else {
						// if just splitting property view, then remove entire copied page
						html.destroyElement(eCopy);
						aPages.pop();
					}

					return;
				}

				if (bFirst && pageOverhead) {
					// recalculate available space for subsequent pages if there was some extra content on the first page
					imageHeight = imageNode ? imageNode.offsetHeight : 0;
					pageOverhead = eView.offsetHeight - Math.max(imageHeight, columnsNode.offsetHeight);
					nViewHeight = nPageHeight - pageOverhead;

					// fit image based on new view size
					if (imageHeight > nViewHeight) {
						img = query("img", imageNode)[0];
						domStyle.set(img, "maxHeight", (img.offsetHeight - imageHeight + nViewHeight) + "px");
					}
				}

				bFirst = bCanRemoveAll = bRemoveAll = false;
			}
		}
	});

	printUtil.registerViewSplitter("property", Splitter);

	return Splitter;
});
